import { useEffect, useState } from "react";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import { useCompanyContext } from "../context/CompanyContext";

/**
 * Hook to handle data reloading when company changes
 * Invalidates all relevant queries so they refetch with the new company context
 * Returns loading state for company changes
 */
export const useCompanyChange = () => {
  const queryClient = useQueryClient();
  const { activeCompany, isChangingCompany } = useCompanyContext();
  const isFetching = useIsFetching() > 0;
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handleCompanyChange = (event: CustomEvent<{ companyId: string }>) => {
      setIsLoading(true);

      // Invalidate all queries to force refetch with new company context
      queryClient.invalidateQueries();

      // Specifically invalidate common query keys
      queryClient.invalidateQueries({ queryKey: ['applications'] });
      queryClient.invalidateQueries({ queryKey: ['invoicing'] });
      queryClient.invalidateQueries({ queryKey: ['payroll'] });
      queryClient.invalidateQueries({ queryKey: ['salarySacrifice'] });
      queryClient.invalidateQueries({ queryKey: ['companyConfiguration'] });

      console.log('Company changed, invalidating queries for company:', event.detail.companyId);
    };

    window.addEventListener('companyChanged', handleCompanyChange as EventListener);

    return () => {
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener);
    };
  }, [queryClient, activeCompany]);

  // Reset loading when fetching completes
  useEffect(() => {
    if (isLoading && !isFetching && !isChangingCompany) {
      setIsLoading(false);
    }
  }, [isLoading, isFetching, isChangingCompany]);

  return {
    isLoading: isLoading || isChangingCompany || isFetching,
  };
};


